// /explore: grid of coins from GET /api/coins?sort=new|hot&q=, with search, sort, loading/empty/error states.
import { api } from "../lib/api.js";
import { epochName } from "../lib/config.js";
import { age, esc, formatSol, pct, plural, shortAddr } from "../lib/format.js";
import { boot } from "../lib/page.js";
import { observeReveal, stagger } from "../lib/reveal.js";
import { $, $$, coinLogo } from "../lib/ui.js";

const S = { sort: "new", q: "", seq: 0, controller: null, firstRender: true };
const box = $("#coins");
const search = $("#search");

function skeleton() {
  const card = `<div class="coin-card panel" aria-hidden="true">
    <div class="coin-card-head"><span class="skel" style="width:44px;height:44px;border-radius:13px"></span><div class="stack-sm" style="flex:1"><span class="skel" style="width:60%;height:16px"></span><span class="skel" style="width:40%;height:12px"></span></div></div>
    <span class="skel" style="height:34px"></span>
    <span class="skel" style="height:96px;border-radius:14px"></span>
  </div>`;
  return `<div class="coin-grid" data-testid="explore-loading">${card.repeat(6)}</div>`;
}

function card(c) {
  const desc = c.description ? esc(c.description) : "No description yet.";
  return `<a class="coin-card panel" href="/coin/${esc(c.token)}" data-testid="coin-card" data-token="${esc(c.token)}" aria-label="${esc(c.name)} ($${esc(c.symbol)}). Shill it">
    <div class="coin-card-head">
      ${coinLogo(c.logo, c.symbol, 44, c.name)}
      <div class="coin-card-title">
        <div class="coin-card-name">${esc(c.name || "Unnamed coin")}</div>
        <div class="coin-card-sym"><strong>$${esc(c.symbol)}</strong><span aria-hidden="true">·</span><span title="${esc(c.creator)}">by ${esc(shortAddr(c.creator))}</span></div>
      </div>
    </div>
    <p class="coin-card-desc">${desc}</p>
    <div class="mini-stats">
      <div class="mini-stat"><span class="k">Shiller share</span><span class="v">${esc(pct(c.shillBps))}</span></div>
      <div class="mini-stat"><span class="k">Shill pool</span><span class="v">${esc(formatSol(c.shillPoolLamports))} SOL</span></div>
      <div class="mini-stat"><span class="k">Posts 24h</span><span class="v" data-testid="card-posts24h">${esc(String(Number(c.posts24h) || 0))}</span></div>
      <div class="mini-stat"><span class="k">Age</span><span class="v">${esc(age(c.launchedAt) || "—")}</span></div>
    </div>
    <div class="coin-card-foot">
      <span>${esc(plural(Number(c.postsTotal ?? c.verifiedPosts) || 0, "verified post"))}</span>
      <span class="btn primary sm" aria-hidden="true">Shill</span>
    </div>
  </a>`;
}

function empty(title, body, action = "") {
  return `<div class="empty" data-testid="explore-empty"><h3>${esc(title)}</h3><p>${esc(body)}</p>${action}</div>`;
}

async function load() {
  const seq = ++S.seq;
  if (S.controller) S.controller.abort();
  S.controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  box.setAttribute("aria-busy", "true");
  if (!box.querySelector(".coin-grid") || S.firstRender) box.innerHTML = skeleton();
  try {
    const list = await api.coins({ sort: S.sort, q: S.q }, { signal: S.controller ? S.controller.signal : undefined });
    if (seq !== S.seq) return;
    const coins = Array.isArray(list) ? list.filter((c) => c && c.token) : [];
    if (!coins.length) {
      box.innerHTML = S.q
        ? empty("No coins match that search", `Nothing found for “${S.q}”. Try a different name, ticker or address.`, '<button type="button" class="btn ghost sm" id="clearSearch">Clear search</button>')
        : empty("No coins yet", "Be the first to launch one through Shilled. Its shillers start earning from the first trade.", '<a class="btn primary sm" href="/launch">Launch a coin</a>');
      $("#clearSearch")?.addEventListener("click", () => { search.value = ""; S.q = ""; load(); });
    } else {
      const grid = document.createElement("div");
      grid.className = "coin-grid";
      grid.innerHTML = coins.map(card).join("");
      if (S.firstRender) {
        grid.classList.add("reveal");
        stagger(grid.children);
      }
      box.replaceChildren(grid);
      if (S.firstRender) observeReveal(grid);
      S.firstRender = false;
    }
  } catch (e) {
    if (e && e.name === "AbortError") return;
    if (seq !== S.seq) return;
    box.innerHTML = empty("Couldn't load coins", e.message || "The server didn't answer. Try again in a moment.", '<button type="button" class="btn ghost sm" id="retry" data-testid="explore-retry">Try again</button>');
    $("#retry")?.addEventListener("click", load);
  } finally {
    if (seq === S.seq) box.removeAttribute("aria-busy");
  }
}

function wire() {
  let t;
  search.addEventListener("input", () => {
    clearTimeout(t);
    t = setTimeout(() => {
      const q = search.value.trim();
      if (q === S.q) return;
      S.q = q;
      load();
    }, 250);
  });
  search.addEventListener("keydown", (e) => { if (e.key === "Enter") { clearTimeout(t); S.q = search.value.trim(); load(); } });
  $$(".chip[data-sort]").forEach((b) => b.addEventListener("click", () => {
    if (S.sort === b.dataset.sort) return;
    S.sort = b.dataset.sort;
    $$(".chip[data-sort]").forEach((x) => x.setAttribute("aria-pressed", String(x === b)));
    load();
  }));
}

async function main() {
  const params = new URLSearchParams(location.search);
  if (params.get("q")) { S.q = params.get("q"); search.value = S.q; }
  if (params.get("sort") === "hot") { S.sort = "hot"; $("#sortHot").setAttribute("aria-pressed", "true"); $("#sortNew").setAttribute("aria-pressed", "false"); }
  wire();
  const cfg = boot();
  load();
  const c = await cfg;
  document.querySelectorAll("[data-epoch-minutes]").forEach((el) => { el.textContent = String(c.epochMinutes); });
  document.querySelectorAll("[data-epoch-name]").forEach((el) => { el.textContent = epochName(c); });
}

main().catch((e) => console.error(e));
